import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BacktestEngine,
  type BacktestMetrics,
  type Candle,
} from "@/lib/backtest/backtestEngine";
import {
  WalkForwardEngine,
  type WalkForwardReport,
} from "@/lib/backtest/walkForwardEngine";

export const Route = createFileRoute("/_authenticated/backtest")({
  ssr: false,
  component: BacktestPage,
});

const PAIRS = [
  "EURUSD",
  "GBPUSD",
  "USDJPY",
  "USDCHF",
  "AUDUSD",
  "USDCAD",
  "NZDUSD",
  "XAUUSD",
];

function generateSyntheticCandles(pair: string, bars: number, seed = 42): Candle[] {
  // Deterministic pseudo-random walk so users see consistent results without paying for tick data
  let s = seed + pair.charCodeAt(0) + pair.charCodeAt(1);
  const rand = () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
  const startPrice = pair.includes("JPY") ? 150 : pair.includes("XAU") ? 2350 : 1.1;
  const vol = pair.includes("XAU") ? 5 : pair.includes("JPY") ? 0.4 : 0.003;
  const out: Candle[] = [];
  let price = startPrice;
  const startTs = Date.now() - bars * 3600_000;
  for (let i = 0; i < bars; i++) {
    const drift = (rand() - 0.5) * vol;
    const open = price;
    const close = price + drift;
    const high = Math.max(open, close) + rand() * vol * 0.5;
    const low = Math.min(open, close) - rand() * vol * 0.5;
    out.push({
      timestamp: startTs + i * 3600_000,
      open,
      high,
      low,
      close,
      volume: 500 + Math.floor(rand() * 1500),
    });
    price = close;
  }
  return out;
}

function BacktestPage() {
  const [pair, setPair] = useState("EURUSD");
  const [balance, setBalance] = useState(10000);
  const [minConfluence, setMinConfluence] = useState(75);
  const [bars, setBars] = useState(1500);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BacktestMetrics | null>(null);
  const [wfReport, setWfReport] = useState<WalkForwardReport | null>(null);

  const run = async () => {
    setRunning(true);
    setResult(null);
    setWfReport(null);
    await new Promise((r) => setTimeout(r, 50));
    const candles = generateSyntheticCandles(pair, bars);
    const engine = new BacktestEngine(balance);
    const metrics = engine.runBacktest(candles, pair, minConfluence);
    setResult(metrics);

    // Run walk-forward only when we have enough bars (>= 1 IS+OOS window)
    const inSampleBars = Math.min(Math.floor(bars * 0.6), 8640);
    const oosBars = Math.max(120, Math.floor(inSampleBars / 3));
    if (candles.length >= inSampleBars + oosBars) {
      const wf = new WalkForwardEngine({
        inSampleBars,
        outOfSampleBars: oosBars,
        initialBalance: balance,
        minConfluence,
      });
      setWfReport(wf.run(candles, pair));
    }
    setRunning(false);
  };

  const equityData = useMemo(
    () =>
      result?.equityCurve.map((v, i) => ({
        i,
        balance: parseFloat(v.toFixed(2)),
        base: balance,
      })) ?? [],
    [result, balance],
  );

  const recentTrades = useMemo(() => result?.trades.slice(-50).reverse() ?? [], [result]);

  return (
    <main className="flex-1 p-4 lg:p-6">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold">
          📊 Technical Strategy Backtest
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Tests an EMA/RSI breakout strategy against synthetic and historical price bars.
        </p>
        <div className="mt-3 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs text-yellow-200">
          <strong>Not council validation.</strong> This lab does NOT execute the 11-agent
          AI council on each bar. Use it to stress-test a technical strategy under varied
          conditions — not as proof of live council performance. Council backtesting is
          in development.
        </div>
      </div>

      <Card className="mb-6 border-border/60 bg-card/60 p-6">
        <h2 className="mb-4 text-xs uppercase tracking-widest text-muted-foreground">
          Configuration
        </h2>
        <div className="grid gap-4 lg:grid-cols-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Pair</Label>
            <Select value={pair} onValueChange={setPair}>
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAIRS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              Initial balance
            </Label>
            <Input
              type="number"
              className="mt-1.5 font-mono"
              value={balance}
              onChange={(e) => setBalance(Number(e.target.value) || 0)}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              Bars (≈ hours)
            </Label>
            <Input
              type="number"
              className="mt-1.5 font-mono"
              value={bars}
              onChange={(e) => setBars(Number(e.target.value) || 500)}
            />
          </div>
          <div className="lg:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              Min confluence — {minConfluence}%
            </Label>
            <Slider
              className="mt-3"
              min={60}
              max={90}
              step={1}
              value={[minConfluence]}
              onValueChange={(v) => setMinConfluence(v[0])}
            />
          </div>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <Button
            size="lg"
            onClick={run}
            disabled={running}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {running ? "Running…" : "Run Backtest"}
          </Button>
          {running && (
            <span className="text-sm text-muted-foreground">
              ⚙️ Running technical strategy across {bars} historical bars…
            </span>
          )}
        </div>
      </Card>

      {result && (
        <>
          {result.winRate > 75 && (
            <div className="mb-6 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-4 text-sm text-yellow-200">
              ⚠️ Results look exceptional. Consider out-of-sample validation before trusting these
              numbers live.
            </div>
          )}

          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <Stat label="Win Rate" value={`${result.winRate}%`} />
            <Stat label="Sharpe" value={result.sharpeRatio.toFixed(2)} />
            <Stat label="Max Drawdown" value={`${result.maxDrawdown}%`} negative />
            <Stat label="Profit Factor" value={result.profitFactor.toFixed(2)} />
            <Stat label="Total Pips" value={result.totalPips.toFixed(1)} />
            <Stat
              label="Final Balance"
              value={`$${result.finalBalance.toLocaleString()}`}
              positive={result.finalBalance >= balance}
              negative={result.finalBalance < balance}
            />
          </div>

          <Card className="mb-6 border-border/60 bg-card/60 p-6">
            <h2 className="mb-4 text-xs uppercase tracking-widest text-muted-foreground">
              Equity curve
            </h2>
            <div className="h-[320px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={equityData}>
                  <defs>
                    <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={result.finalBalance >= balance ? "#22c55e" : "#ef4444"}
                        stopOpacity={0.4}
                      />
                      <stop
                        offset="100%"
                        stopColor={result.finalBalance >= balance ? "#22c55e" : "#ef4444"}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="i" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} domain={["auto", "auto"]} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke={result.finalBalance >= balance ? "#22c55e" : "#ef4444"}
                    fill="url(#eq)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="border-border/60 bg-card/60 p-6">
            <h2 className="mb-4 text-xs uppercase tracking-widest text-muted-foreground">
              Last {recentTrades.length} trades
            </h2>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Direction</TableHead>
                    <TableHead>Entry</TableHead>
                    <TableHead>Exit</TableHead>
                    <TableHead>Pips</TableHead>
                    <TableHead>P&amp;L</TableHead>
                    <TableHead>Agents</TableHead>
                    <TableHead>Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentTrades.map((t, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">
                        {new Date(t.entryTime).toISOString().slice(0, 16).replace("T", " ")}
                      </TableCell>
                      <TableCell
                        className={t.direction === "BUY" ? "text-buy" : "text-destructive"}
                      >
                        {t.direction}
                      </TableCell>
                      <TableCell className="font-mono">{t.entryPrice.toFixed(5)}</TableCell>
                      <TableCell className="font-mono">{t.exitPrice.toFixed(5)}</TableCell>
                      <TableCell
                        className={`font-mono ${t.pnlPips > 0 ? "text-buy" : "text-destructive"}`}
                      >
                        {t.pnlPips.toFixed(1)}
                      </TableCell>
                      <TableCell
                        className={`font-mono ${t.pnlDollar > 0 ? "text-buy" : "text-destructive"}`}
                      >
                        ${t.pnlDollar.toFixed(2)}
                      </TableCell>
                      <TableCell>{t.agentsAgreed}</TableCell>
                      <TableCell>{t.result === "WIN" ? "✅" : "❌"}</TableCell>
                    </TableRow>
                  ))}
                  {recentTrades.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="py-6 text-center text-muted-foreground">
                        No trades met the confluence threshold. Try lowering it.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          {wfReport && <WalkForwardSection report={wfReport} />}
        </>
      )}
    </main>
  );
}

function WalkForwardSection({ report }: { report: WalkForwardReport }) {
  const verdictMeta = {
    VALIDATED_ALPHA: {
      border: "border-buy/60",
      bg: "bg-buy/5",
      text: "text-buy",
      icon: "✅",
      label: "Statistical Edge Confirmed",
    },
    INSIGNIFICANT_SAMPLE: {
      border: "border-yellow-500/60",
      bg: "bg-yellow-500/5",
      text: "text-yellow-300",
      icon: "⚠️",
      label: "Need more trades for significance",
    },
    OVERFITTED_DECAY: {
      border: "border-destructive/60",
      bg: "bg-destructive/5",
      text: "text-destructive",
      icon: "❌",
      label: "Strategy shows overfitting",
    },
  }[report.verdict];

  const allStarts = report.windows.map((w) => w.inSampleStart);
  const allEnds = report.windows.map((w) => w.outOfSampleEnd);
  const minTs = Math.min(...allStarts);
  const maxTs = Math.max(...allEnds);
  const range = Math.max(maxTs - minTs, 1);

  const years = (() => {
    const yrs = new Set<number>();
    for (const w of report.windows) {
      yrs.add(new Date(w.inSampleStart).getUTCFullYear());
      yrs.add(new Date(w.outOfSampleEnd).getUTCFullYear());
    }
    return Array.from(yrs).sort();
  })();

  return (
    <Card className={`mt-6 border-2 ${verdictMeta.border} ${verdictMeta.bg} p-6`}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">🔬 Walk-Forward Validation</h2>
        <span className={`text-sm font-semibold ${verdictMeta.text}`}>
          {verdictMeta.icon} {verdictMeta.label}
        </span>
      </div>

      <pre className="mb-6 overflow-x-auto rounded-md border border-border/60 bg-background/40 p-4 font-mono text-xs leading-6">
{`STATISTICAL VALIDATION REPORT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Status:        ${report.verdict} ${verdictMeta.icon}
Sharpe Ratio:  ${report.aggregateSharpe.toFixed(2)}
T-Statistic:   ${report.tStatistic.toFixed(4)}
Total Trades:  ${report.totalTrades}
Min Required:  ${report.minRequiredTrades}
Decay Ratio:   ${report.decayRatio.toFixed(2)}  (OOS/IS Sharpe)
Confidence:    ${Math.round(report.confidenceLevel * 100)}%
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`}
      </pre>

      <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">
        Rolling windows
      </div>

      {/* Year axis */}
      <div className="relative mb-2 h-5 border-b border-border/60">
        {years.map((y) => {
          const yTs = Date.UTC(y, 0, 1);
          const left = ((yTs - minTs) / range) * 100;
          if (left < 0 || left > 100) return null;
          return (
            <div
              key={y}
              className="absolute -translate-x-1/2 text-[10px] font-mono text-muted-foreground"
              style={{ left: `${left}%` }}
            >
              {y}
            </div>
          );
        })}
      </div>

      <div className="space-y-2">
        {report.windows.map((w) => {
          const isLeft = ((w.inSampleStart - minTs) / range) * 100;
          const isWidth = ((w.inSampleEnd - w.inSampleStart) / range) * 100;
          const oosLeft = ((w.outOfSampleStart - minTs) / range) * 100;
          const oosWidth = ((w.outOfSampleEnd - w.outOfSampleStart) / range) * 100;
          const oos = w.outOfSample;
          return (
            <div key={w.index} className="group">
              <div className="relative h-6">
                <div
                  className="absolute top-0 h-6 rounded-sm border border-primary/40 bg-primary/15 px-2 text-[10px] font-mono leading-6 text-primary"
                  style={{ left: `${isLeft}%`, width: `${isWidth}%` }}
                  title="In-Sample"
                >
                  IS
                </div>
                <div
                  className={`absolute top-0 h-6 rounded-sm border px-2 text-[10px] font-mono leading-6 ${
                    oos.winRate >= 50
                      ? "border-buy/60 bg-buy/20 text-buy"
                      : "border-destructive/60 bg-destructive/20 text-destructive"
                  }`}
                  style={{ left: `${oosLeft}%`, width: `${oosWidth}%` }}
                  title="Out-of-Sample"
                >
                  OOS
                </div>
              </div>
              <div className="mt-1 flex gap-4 pl-1 text-[11px] font-mono text-muted-foreground">
                <span>W{w.index + 1}</span>
                <span>Trades: {oos.totalTrades}</span>
                <span>Win: {oos.winRate}%</span>
                <span>Sharpe: {oos.sharpeRatio.toFixed(2)}</span>
                <span>PF: {oos.profitFactor.toFixed(2)}</span>
              </div>
            </div>
          );
        })}
        {report.windows.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Not enough bars for walk-forward analysis. Increase bar count.
          </div>
        )}
      </div>
    </Card>
  );
}

function Stat({
  label,
  value,
  positive,
  negative,
}: {
  label: string;
  value: string;
  positive?: boolean;
  negative?: boolean;
}) {
  return (
    <Card className="border-border/60 bg-card/60 p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div
        className={`mt-2 font-display text-xl font-bold ${
          positive ? "text-buy" : negative ? "text-destructive" : ""
        }`}
      >
        {value}
      </div>
    </Card>
  );
}
